import {
	getPropertySortText,
	NoResults,
	SelectOption,
	ScrollableVirtualList,
	ScrollableVirtualListHandle,
	MultiSelectOption
} from "client/Components";
import {
	DataSourceSortableState,
	DataSourceSortDirection
} from "client/Data/sorting";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	createRemoteDataSource,
	LazyLoadCursorKind,
	MediaQueryKind,
	RemoteDataSourceState,
	useAlerts,
	useUserTimezone,
	useValueEqualMemo,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	isNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	getIsFullTextSearchEnabled,
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind,
	KnowledgeItemGlobalSearchResultItem,
	SearchIntegrationKind,
	searchKnowledgeItems,
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { ScopeLayout } from "../ScopeLayout";
import { SearchSettings } from "../SearchSettings";
import {
	listAuthors,
	KnowledgeItemOrderModel,
	KnowledgeItemPreviewItemModel,
	KnowledgeItemSortProperty
} from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { HubItemCategoryModel, listCategories } from "client/Api/Hub/hub";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { getComputedDisplayName } from "../../AddressBook/common";
import { SortDirection } from "client/Api/common";
import { never } from "client_legacy/ndaw/errors";
import { SearchNoResults } from "../SearchNoResults";
import { TimeZone } from "client/DateAndTime/timezones";
import { checkHasFilter } from "./common";
import { KnowledgeItemView } from "../Items/KnowledgeItemView";
import { Guid } from "client_legacy/ndaw/guid";

export type KnowledgeItemSearchProps = {
	searchTerm: VersionedValue<string>;
	mobileSearchSettingsTarget: HTMLDivElement | null;
};

export const KnowledgeItemSearch = (props: KnowledgeItemSearchProps): ReactElement => {
	const {
		searchTerm,
		mobileSearchSettingsTarget
	} = props;

	const [isLoadingSort, setIsLoadingSort] = useState<boolean>(true);
	const [isElasticSearchEnabled, setIsElasticSearchEnabled] = useState<boolean>(false);
	const [isLoadingFilters, setIsLoadingFilters] = useState<boolean>(false);
	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);
	const [categoryOptions, setCategoryOptions] = useState<readonly MultiSelectOption<number>[]>(EMPTY_ARRAY);
	const [authorOptions, setAuthorOptions] = useState<readonly MultiSelectOption<number>[]>(EMPTY_ARRAY);
	const [formData, setFormData] = useState<KnowledgeItemSearchFormData>({
		...defaultFilters,
		sort: {
			direction: DataSourceSortDirection.Descending,
			propertyName: "knowledgeItem.viewsCount"
		}
	});

	const listHandle: RefObject<ScrollableVirtualListHandle<KnowledgeItemGlobalSearchResultItem> | null> = useRef<ScrollableVirtualListHandle<KnowledgeItemGlobalSearchResultItem>>(null);

	const alertManager: AlertManager = useAlerts();

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const userTimezone: TimeZone = useUserTimezone();

	const formHandler: IFormHandler<KnowledgeItemSearchFormData> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const {
		sort: _,
		...filters
	} = formData;

	const hasFilter: boolean = checkHasFilter<FilterModel>(filters, defaultFilters);

	const partialDataSourceOptions = useValueEqualMemo(
		(): Omit<Parameters<typeof useDataSource>[0], "desiredAmountOfItems"> => ({
			searchTerm: searchTerm
				.value,
			filtering: {
				categoryIds: formData.categoryIds,
				authorIds: formData.authorIds,
			},
			sorting: formData.sort,
			globalContext: {
				isLoadingSort,
				searchVersion: searchTerm.version
			}
		}),
		[
			formData.authorIds,
			formData.categoryIds,
			formData.sort,
			searchTerm.value,
			searchTerm.version,
			isLoadingSort
		]
	);

	const {
		currentItems,
		hasMoreOnServer,
		reload,
		getLoadingState
	} = useDataSource({
		...partialDataSourceOptions,
		desiredAmountOfItems,
	});

	const sortOptions = useMemo(
		(): readonly SelectOption<SortValue>[] => isLoadingSort
			? EMPTY_ARRAY
			: getSortOptions(isElasticSearchEnabled),
		[
			isLoadingSort,
			isElasticSearchEnabled
		]
	);

	const loadMoreHandler = useCallback(
		(): void => {
			setDesiredAmountOfItems((x) => x + PAGE_SIZE);
		},
		[]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(partialDataSourceOptions);
			setDesiredAmountOfItems(PAGE_SIZE);
			listHandle.current
				?.scrollToTop();
		},
		[partialDataSourceOptions]
	);

	const clearHandler = useCallback(
		(): void => {
			setFormData(
				(x) => ({
					...x,
					...defaultFilters
				})
			);

			formHandler.validation
				.triggerValueChanged();
		},
		[formHandler.validation]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsLoadingSort(true);
					const isEnabled: boolean = await getIsFullTextSearchEnabled(signal);
					setIsElasticSearchEnabled(isEnabled);

					if (isEnabled) {
						setFormData(data => ({
							...data,
							sort: {
								...data.sort,
								propertyName: "knowledgeItem.relevanceScore"
							}
						}));
					}

					setIsLoadingSort(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
					setIsLoadingSort(false);
				}
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsLoadingFilters(true);
					const categoriesRequest: Promise<readonly HubItemCategoryModel[]> =
						listCategories(HubItemKind.KnowledgeItem, signal);

					const authorsRequest: Promise<readonly UserIdentityModel[]> =
						listAuthors(signal);

					const [
						categories,
						authors
					] = await Promise.all([
						categoriesRequest,
						authorsRequest
					]);

					setCategoryOptions(categories.map(convertCategoryToOption));
					setAuthorOptions(authors.map(convertAuthorToOption));
					setIsLoadingFilters(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsLoadingFilters(false);
					console.error(e);
				}
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager]
	);

	const renderContent = (): ReactElement => {
		const loadingState: RemoteDataSourceState = getLoadingState();

		if (loadingState === "ready" && currentItems.length === 0 && !isLoadingSort) {
			return <SearchNoResults />;
		}

		if (loadingState === "loading" || currentItems.length > 0 || isLoadingFilters) {
			return (
				<ScrollableVirtualList
					items={currentItems}
					estimatedHeightOfAnItem={130}
					itemKey={getUniqueKey}
					ref={listHandle}
					trackingId="knowledge-item-search-results"
					renderAfterItems={
						<>
							<ScrollableVirtualList.LoadMoreIndicator
								isLoadingData={loadingState === "loading" || isLoadingSort}
								hasMoreItems={hasMoreOnServer}
								onRequestMoreItems={loadMoreHandler}
							/>
							<ScrollableVirtualList.EndOfListSpacing />
						</>
					}
				>
					{(item: KnowledgeItemGlobalSearchResultItem, index: number): ReactElement => {
						return (
							<KnowledgeItemView
								navigationIndex={index}
								item={item}
								itemScope={knowledgeItemSearchScope}
								isMobile={isMobile}
								timezone={userTimezone}
							/>
						);
					}}
				</ScrollableVirtualList>
			);
		}

		return (
			<NoResults
				trackingId="has-error"
				icon="gear"
				title={localize("_NotAbleToRetrieveSearchResults")}
				message={localize("_WeWereUnableToRetrieveSearchResults")}
				onRetry={reload}
				height="fill"
			/>
		);
	};

	return (
		<ScopeLayout>
			<ScopeLayout.Settings>
				<SearchSettings
					menuTarget={mobileSearchSettingsTarget}
					onClear={clearHandler}
					hasFilter={hasFilter}
				>
					<SearchSettings.Select
						label={localize("_SortBy")}
						attachable={formHandler.for("sort")}
						options={sortOptions}
						placeholder={localize("_SelectSortingOption")}
						hasBusyIndicator={isLoadingSort}
						isExpandedByDefault
					/>

					<SearchSettings.MultiSelect
						label={localize("_Category")}
						attachable={formHandler.for("categoryIds")}
						options={categoryOptions}
						placeholder={localize("_SelectCategory(ies)")}
						hasBusyIndicator={isLoadingFilters}
					/>

					<SearchSettings.MultiSelect
						label={localize("_Author")}
						attachable={formHandler.for("authorIds")}
						options={authorOptions}
						placeholder={localize("_SelectAuthor(s)")}
						hasBusyIndicator={isLoadingFilters}
					/>
				</SearchSettings>

			</ScopeLayout.Settings>

			<ScopeLayout.Results>
				{renderContent()}
			</ScopeLayout.Results>
		</ScopeLayout>
	);
};
KnowledgeItemSearch.displayName = "KnowledgeItemSearch";

export const PAGE_SIZE = 20;

type SortValue = {
	direction: Exclude<DataSourceSortDirection, DataSourceSortDirection.None>;
	propertyName: PropertyPath<KnowledgeItemGlobalSearchResultItem> & (
		| "knowledgeItem.viewsCount"
		| "knowledgeItem.relevanceScore"
		| "knowledgeItem.publicationDate"
		| "knowledgeItem.title"
	);
};

type KnowledgeItemSearchFormData = {
	sort: SortValue;
	categoryIds: readonly number[];
	authorIds: readonly number[];
};

type FilterModel = Omit<KnowledgeItemSearchFormData, "sort">;

const defaultFilters: FilterModel = {
	categoryIds: EMPTY_ARRAY,
	authorIds: EMPTY_ARRAY
};

type FilterContext = {
	categoryIds: readonly number[];
	authorIds: readonly number[];
};

type GlobalContext = {
	isLoadingSort: boolean;
	// Trigger new request when searchVersion changes
	searchVersion: Guid;
};

const useDataSource = createRemoteDataSource<
	KnowledgeItemGlobalSearchResultItem,
	FilterContext,
	GlobalContext
>()
	.setConfiguration({
		hasSearch: true,
		hasFiltering: true,
		lazyLoadCursor: LazyLoadCursorKind.AfterLastItem
	})
	.getHook(
		{
			performRequest: async (options): Promise<LazyLoadPageResult<KnowledgeItemGlobalSearchResultItem>> => {
				const {
					searchTerm,
					filterState,
					amount,
					signal,
					after,
					sorting,
					globalContext
				} = options;

				const {
					authorIds,
					categoryIds
				} = filterState;

				if (searchTerm.trim() === "" || globalContext.isLoadingSort) {
					return {
						hasMore: false,
						items: []
					};
				} else {
					const lastKnowledgeItem: KnowledgeItemPreviewItemModel | undefined =
						 after?.knowledgeItem;

					const backendResult: LazyLoadPageResult<KnowledgeItemGlobalSearchResultItem> =
						await searchKnowledgeItems(
							{
								searchTerm,
								authorIds,
								categoryIds,
								amount,
								sortOrder: convertSort(ensureNotNil(sorting)),
								pagingInformation: isNil(lastKnowledgeItem)
									? {
										isSet: false
									}
									: {
										isSet: true,
										value: {
											id: lastKnowledgeItem.id,
											pinDate: lastKnowledgeItem.pinDate,
											publicationDate: lastKnowledgeItem.publicationDate,
											title: lastKnowledgeItem.title,
											viewsCount: lastKnowledgeItem.viewsCount,
											relevanceScore: lastKnowledgeItem.relevanceScore
										}
									}
							},
							signal
						);

					const {
						items,
						hasMore
					} = backendResult;

					return {
						items,
						hasMore
					};
				}
			}
		}
	);

function convertSort(
	sortingState: DataSourceSortableState<KnowledgeItemGlobalSearchResultItem>
): KnowledgeItemOrderModel {
	if (!sortingState) {
		return {
			property: KnowledgeItemSortProperty.Views,
			direction: SortDirection.Ascending
		};
	}

	if (sortingState.direction === DataSourceSortDirection.None) {
		throw new Error("sorting directions is not specified.");
	}

	let property: KnowledgeItemSortProperty;

	switch(sortingState.propertyName) {
		case "knowledgeItem.viewsCount":
			property = KnowledgeItemSortProperty.Views;
			break;
		case "knowledgeItem.relevanceScore":
			property = KnowledgeItemSortProperty.Relevance;
			break;
		case "knowledgeItem.publicationDate":
			property = KnowledgeItemSortProperty.PublicationDate;
			break;
		case "knowledgeItem.title":
			property = KnowledgeItemSortProperty.Title;
			break;
		default:
			throw new Error(`Sorting on ${sortingState.propertyName} is not supported`);
	}

	const direction: SortDirection = sortingState.direction === DataSourceSortDirection.Ascending
		? SortDirection.Ascending
		: sortingState.direction === DataSourceSortDirection.Descending
			? SortDirection.Descending
			: never(sortingState.direction);

	return {
		property,
		direction
	};
}

function getUniqueKey(item: KnowledgeItemGlobalSearchResultItem): string {
	return item.knowledgeItem
		.id
		.toString();
}

function getSortOptions(
	isElasticSearchEnabled: boolean
): readonly SelectOption<SortValue>[] {
	const defaultSortValue: SortValue = {
		direction: DataSourceSortDirection.Descending,
		propertyName: isElasticSearchEnabled
			? "knowledgeItem.relevanceScore"
			: "knowledgeItem.viewsCount"
	};

	const defaultSortOption = isElasticSearchEnabled
		? {
			text: localize("_MostRelevant"),
			value: defaultSortValue,
			valueTrackingId: "most-relevant"
		} : {
			text: localize("_MostPopular"),
			value: defaultSortValue,
			valueTrackingId: "most-popular"
		};

	return [
		defaultSortOption,
		...staticSortOptions
	];
}

const staticSortOptions: readonly SelectOption<SortValue>[] = [
	{
		text: getPropertySortText("_PublicationDate", "_NewToOld"),
		value: { propertyName: "knowledgeItem.publicationDate", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "publication-date-new-old"
	},
	{
		text: getPropertySortText("_PublicationDate", "_OldToNew"),
		value: { propertyName: "knowledgeItem.publicationDate", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "publication-date-old-new"
	},
	{
		text: getPropertySortText("_Title", "_AToZ"),
		value: { propertyName: "knowledgeItem.title", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "title-a-z"
	},
	{
		text: getPropertySortText("_Title", "_ZToA"),
		value: { propertyName: "knowledgeItem.title", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "title-z-a"
	},
];

function convertCategoryToOption(category: HubItemCategoryModel): SelectOption<number> {
	return {
		text: category.name,
		value: category.id
	};
}

function convertAuthorToOption(author: UserIdentityModel): SelectOption<number> {
	const {
		computedDisplayName
	} = getComputedDisplayName(author);

	return {
		text: computedDisplayName,
		value: author.userId
	};
}

const knowledgeItemSearchScope: GlobalSearchScopeIdentifier = {
	kind: GlobalSearchScopeKind.Predefined,
	predefinedKind: SearchIntegrationKind.KnowledgeItems
};
